import { Injectable, signal, computed, inject } from '@angular/core';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  updateProfile as updateFirebaseProfile,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { Router } from '@angular/router';
import { auth, db } from './firebase.init';
import { UserProfile, Address } from '../models/types';
import { CartService } from './cart.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private cartService = inject(CartService);
  private router = inject(Router);
  private userSignal = signal<UserProfile | null>(null);
  private loadingSignal = signal<boolean>(true);

  // Read-only signals
  readonly user = computed(() => this.userSignal());
  readonly isAuthenticated = computed(() => this.userSignal() !== null);
  readonly isAdmin = computed(() => this.userSignal()?.role === 'admin');
  readonly loading = computed(() => this.loadingSignal());

  private unsubscribeUserListener?: () => void;

  constructor() {
    this.initAuthState();
  }

  private initAuthState() {
    onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      this.loadingSignal.set(true);
      if (this.unsubscribeUserListener) {
        this.unsubscribeUserListener();
        this.unsubscribeUserListener = undefined;
      }

      if (firebaseUser) {
        // Set up real-time listener for the user's Firestore profile document
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        this.unsubscribeUserListener = onSnapshot(userDocRef, async (docSnap) => {
          if (docSnap.exists()) {
            const profile = docSnap.data() as UserProfile;
            if (profile.disabled) {
              this.cartService.clearCart();
              await signOut(auth);
              this.userSignal.set(null);
              this.router.navigate(['/home']);
              alert('Вашият профил е деактивиран от администратора.');
            } else {
              this.userSignal.set(profile);
            }
          } else {
            // Profile document does not exist in Firestore yet (do not write default placeholder to DB)
            this.userSignal.set(null);
          }
          this.loadingSignal.set(false);
        }, (error) => {
          console.error('Error listening to user doc: ', error);
          this.loadingSignal.set(false);
        });
      } else {
        this.userSignal.set(null);
        this.loadingSignal.set(false);
      }
    });
  }

  // Login
  login(email: string, password: string) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Registration with starting address
  async register(email: string, password: string, displayName: string, phone: string, initialAddress: Omit<Address, 'id' | 'is_default'>) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const uid = userCredential.user.uid;

    const firstAddress: Address = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2),
      location: initialAddress.location,
      street: initialAddress.street,
      number: initialAddress.number,
      details: initialAddress.details || '',
      is_default: true
    };

    const userProfile: UserProfile = {
      uid,
      email,
      displayName,
      phone,
      addresses: [firstAddress],
      role: 'customer',
      created_at: new Date()
    };

    // Save profile to database
    await setDoc(doc(db, 'users', uid), userProfile);
    this.userSignal.set(userProfile);
    return userCredential;
  }

  // Send Password Reset Link
  sendPasswordReset(email: string) {
    return sendPasswordResetEmail(auth, email);
  }

  // Logout
  async logout() {
    this.cartService.clearCart();
    await signOut(auth);
    this.router.navigate(['/home']);
  }

  // Manage Addresses
  async addAddress(addressData: Omit<Address, 'id' | 'is_default'>) {
    const currentUser = this.userSignal();
    if (!currentUser) throw new Error('User not logged in');

    const newAddress: Address = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2),
      location: addressData.location,
      street: addressData.street,
      number: addressData.number,
      details: addressData.details || '',
      is_default: currentUser.addresses.length === 0 // Default if it's the first address
    };

    const updatedAddresses = [...currentUser.addresses, newAddress];
    await updateDoc(doc(db, 'users', currentUser.uid), {
      addresses: updatedAddresses
    });
  }

  async deleteAddress(addressId: string) {
    const currentUser = this.userSignal();
    if (!currentUser) throw new Error('User not logged in');

    let updatedAddresses = currentUser.addresses.filter(addr => addr.id !== addressId);
    
    // If we deleted the default address and there are others, set the first one as default
    if (currentUser.addresses.find(addr => addr.id === addressId)?.is_default && updatedAddresses.length > 0) {
      updatedAddresses[0].is_default = true;
    }

    await updateDoc(doc(db, 'users', currentUser.uid), {
      addresses: updatedAddresses
    });
  }

  async setDefaultAddress(addressId: string) {
    const currentUser = this.userSignal();
    if (!currentUser) throw new Error('User not logged in');

    const updatedAddresses = currentUser.addresses.map(addr => ({
      ...addr,
      is_default: addr.id === addressId
    }));

    await updateDoc(doc(db, 'users', currentUser.uid), {
      addresses: updatedAddresses
    });
  }

  async updateProfile(displayName: string, phone: string) {
    const currentUser = this.userSignal();
    if (!currentUser) throw new Error('User not logged in');

    if (auth.currentUser) {
      await updateFirebaseProfile(auth.currentUser, { displayName });
    }

    await updateDoc(doc(db, 'users', currentUser.uid), {
      displayName: displayName.trim(),
      phone: phone.trim()
    });
  }
}
